﻿import { AccessorialState } from '@store/ltl';
import { ltlQuoteState } from '@store/ltl/store';
import { orderStore } from '@store/orders';
import { LtlAccessorialCatalogItem } from '@store/orders/types';
import { IAddress } from '@typeDefinitions/addressTypes';
import { remappedCountryCode } from '@utils/ltlUtils';
import {
  AccessorialsType,
  qAccessorial,
} from '@views/ltl/quoting/requoting/types';
import { setType } from '@views/ltl/quoting/types';
import { ItemAccessorial } from '@views/ltl/types';
import { sortBy } from 'lodash';
import { SetStoreFunction } from 'solid-js/store';

type AccessorialRecord = {
  accessorialId?: string | number | null;
  // New LTL accessorial schema's numeric catalog id (e.g. customer address
  // accessorials now return this alongside the legacy GUID accessorialId).
  ltlAccessorialId?: string | number | null;
  accessorialType?: number | string | null;
  accessorial?: {
    id?: string | number | null;
    accessorialType?: {
      id?: number | string | null;
    } | null;
    accessorialTypeId?: number | string | null;
  } | null;
};

export function mapCountryCode(country: string | undefined | null): string {
  switch (country) {
    case 'CAN':
      return 'CA';
    case 'MEX':
      return 'MX';
    case 'USA':
      return 'US';
    default:
      return country ?? '';
  }
}

function normalizeAccessorialRecords(value: unknown): AccessorialRecord[] {
  if (Array.isArray(value)) {
    return value as AccessorialRecord[];
  }

  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (Array.isArray(parsed)) {
        return parsed as AccessorialRecord[];
      }
    } catch {
      return [];
    }
  }

  return [];
}

function normalizeAccessorialIds(item: AccessorialRecord): string[] {
  const candidates = [
    item.accessorialId,
    item.ltlAccessorialId,
    item.accessorial?.id,
  ].filter((value) => value !== null && value !== undefined);

  return Array.from(new Set(candidates.map((value) => String(value))));
}

function normalizeAccessorialType(item: AccessorialRecord): number | null {
  const rawType =
    item.accessorialType ??
    item.accessorial?.accessorialType?.id ??
    item.accessorial?.accessorialTypeId;

  if (typeof rawType === 'number') {
    return rawType;
  }

  if (typeof rawType === 'string' && rawType.trim() !== '') {
    const parsedType = Number(rawType);
    return Number.isNaN(parsedType) ? null : parsedType;
  }

  return null;
}

export function getAccessorialIdSetsByType(accessorials: unknown): {
  pickupAccessorialIds: Set<string>;
  deliveryAccessorialIds: Set<string>;
} {
  const pickupAccessorialIds = new Set<string>();
  const deliveryAccessorialIds = new Set<string>();

  normalizeAccessorialRecords(accessorials).forEach((item) => {
    const accessorialIds = normalizeAccessorialIds(item);
    const accessorialType = normalizeAccessorialType(item);

    if (accessorialIds.length === 0 || accessorialType === null) {
      return;
    }

    if (accessorialType === 1) {
      accessorialIds.forEach((id) => pickupAccessorialIds.add(id));
    }

    if (accessorialType === 2) {
      accessorialIds.forEach((id) => deliveryAccessorialIds.add(id));
    }
  });

  return {
    pickupAccessorialIds,
    deliveryAccessorialIds,
  };
}

// Rate quote submission endpoints (e.g. PUT /ltl/Rate) still require the
// legacy GUID accessorialId and haven't been migrated to accept the new
// schema's numeric-only catalog id. Given a selected accessorial's name and
// the legacy GUID-keyed catalog (fetchLegacyLtlAccessorials), resolves the
// matching legacy GUID so it can be sent alongside/instead of whatever id the
// (possibly new-schema) selector list is currently using.
export function findLegacyAccessorialId(
  name: string | null | undefined,
  legacyCatalog: ItemAccessorial[],
): string | undefined {
  if (!name) {
    return undefined;
  }
  const normalizedName = name.trim().toLowerCase();
  return legacyCatalog.find(
    (item) => (item.name ?? '').trim().toLowerCase() === normalizedName,
  )?.id;
}

// The backend derives the new LTL accessorial schema's numeric
// ltlAccessorialId itself from a stable `code`, rather than trusting a
// client-supplied numeric id — codes are shared between the legacy and new
// schemas, so this list can always be populated regardless of whether the
// LtlAccessorialsSchemaRefactorRead flag is on (unlike the numeric id, which
// only exists in the new schema catalog). Given a selected accessorial's
// name and the new schema catalog (fetchLtlAccessorialCatalog, fetched
// independently of the read flag), resolves the matching `code`.
//
// The new schema catalog can have separate rows sharing the same
// displayName but differing accessorialTypeId and code (e.g. "Liftgate"
// pickup vs "Liftgate" delivery are two distinct catalog rows with
// different codes) — so matching by name alone can resolve the wrong code.
// `category` disambiguates which row to use: 'pickup' -> accessorialTypeId
// 1, 'delivery' -> accessorialTypeId 2, 'shipment' -> any other type id
// (global/shipment-level accessorials).
export function findLtlAccessorialCode(
  name: string | null | undefined,
  ltlCatalog: LtlAccessorialCatalogItem[],
  category: 'pickup' | 'delivery' | 'shipment',
): string | undefined {
  if (!name) {
    return undefined;
  }
  const normalizedName = name.trim().toLowerCase();
  const nameMatches = ltlCatalog.filter(
    (item) => item.displayName.trim().toLowerCase() === normalizedName,
  );
  const matchesCategory = (item: LtlAccessorialCatalogItem) => {
    if (category === 'pickup') {
      return item.accessorialTypeId === 1;
    }
    if (category === 'delivery') {
      return item.accessorialTypeId === 2;
    }
    return item.accessorialTypeId !== 1 && item.accessorialTypeId !== 2;
  };

  const categoryMatch = nameMatches.find(matchesCategory);
  if (categoryMatch) {
    return categoryMatch.code;
  }

  // Some accessorials (e.g. "In Bond", "Sort & Segregate") are a single row
  // categorized as Global in the new catalog but Pickup in the legacy one —
  // the checked bucket here is driven by whichever catalog is active, so the
  // requested category may not exist in THIS catalog. Only fall back when
  // the name is unambiguous (exactly one row); names with multiple rows
  // (e.g. Liftgate pickup/delivery) must keep resolving strictly by category.
  return nameMatches.length === 1 ? nameMatches[0].code : undefined;
}

export const getCityStateCountryZipInfo = (
  type: string,
  setfields2: setType,
) => {
  return () => {
    const isOrigin = type === 'Pickup';
    const prefix = isOrigin ? 'origin' : 'destination';
    const orderInfo = orderStore.order;
    let address: Partial<IAddress> = {};

    // Prefer the active order stop values when available.
    const activeStops = orderInfo.loads[orderStore.activeTab.index]?.stops;
    if (Array.isArray(activeStops) && activeStops.length > 0) {
      activeStops.forEach((stop) => {
        const isMatchingStop = isOrigin
          ? Boolean(stop.origin) || Boolean(stop.pickUp)
          : Boolean(stop.destination) || Boolean(stop.dropOff);

        if (isMatchingStop) {
          address = {
            city: stop.city ?? '',
            state: stop.state ?? '',
            country: stop.countryCode ?? 'USA',
            zip: stop.zip,
          };
        }
      });

      if (address.zip) {
        setZipCode(prefix, address, setfields2)();
        return;
      }
    }

    if (ltlQuoteState.quote !== undefined) {
      address = {
        city: isOrigin
          ? (ltlQuoteState.quote.originCity ?? '')
          : (ltlQuoteState.quote.destinationCity ?? ''),
        state: isOrigin
          ? (ltlQuoteState.quote.originState ?? '')
          : (ltlQuoteState.quote.destinationState ?? ''),
        country: isOrigin
          ? mapCountryCode(ltlQuoteState.quote.originCountry)
          : mapCountryCode(ltlQuoteState.quote.destinationCountry),
        zip: isOrigin
          ? (ltlQuoteState.quote.originZip ?? '')
          : (ltlQuoteState.quote.destinationZip ?? ''),
      };
      setZipCode(prefix, address, setfields2)();
      return;
    }
    orderInfo.loads[orderStore.activeTab.index]?.stops?.forEach((stop) => {
      if (
        (isOrigin && Boolean(stop.origin)) ||
        (isOrigin && Boolean(stop.pickUp))
      ) {
        address = {
          city: stop.city ?? '',
          state: stop.state ?? '',
          country: stop.countryCode ?? 'USA',
          zip: stop.zip,
        };
      } else if (
        (!isOrigin && Boolean(stop.destination)) ||
        (!isOrigin && Boolean(stop.dropOff))
      ) {
        address = {
          city: stop.city ?? '',
          state: stop.state ?? '',
          country: stop.countryCode ?? 'USA',
          zip: stop.zip,
        };
      }
    });
    setZipCode(prefix, address, setfields2)();
  };
};

export const getAccessorialsInfo = (
  type: string,
  setAccessorials: SetStoreFunction<AccessorialsType>,
  accessorialStore: AccessorialState,
) => {
  return () => {
    const pickupVals: qAccessorial[] = [];
    const deliveryVals: qAccessorial[] = [];
    const shipmentVals: qAccessorial[] = [];
    // Match by name rather than HAZMAT_ID (a legacy GUID constant): when the
    // LtlAccessorialsSchemaRefactorRead flag is enabled, accessorialStore
    // items are keyed by the new schema's numeric catalog id instead of the
    // legacy GUID, so an id-based lookup would never find Hazmat and leave
    // `hazmat` undefined, breaking "Get Rates" for hazmat shipments.
    const hazmatVals = accessorialStore.items.find(
      (a) => (a.name ?? '').trim().toLowerCase() === 'hazmat',
    ) as ItemAccessorial;

    accessorialStore.items.forEach((x) => {
      const item = {
        checked: false,
        accessorialId: x.id,
        accessorial: x,
      };

      if (Boolean(x.isPickup)) {
        pickupVals.push({ ...item, accessorialType: 1 });
      }

      if (Boolean(x.isDelivery)) {
        deliveryVals.push({ ...item, accessorialType: 2 });
      }

      if (x.isGlobal && x.name !== 'Hazmat') {
        shipmentVals.push({ ...item, accessorialType: 0 });
      }
    });

    //NOTE: LOOK INTO THIS
    setAccessorials('hazmat', hazmatVals);

    switch (type) {
      case 'Pickup':
        pickupVals.forEach((x: qAccessorial) => {
          if (
            (ltlQuoteState.quote?.ltlQuote?.pickupAccessorials ?? []).includes(
              x.accessorial.name!,
            )
          ) {
            x.checked = true;
          }
        });
        setAccessorials('pickup', sortBy(pickupVals, 'accessorial.name'));
      case 'Delivery':
        deliveryVals.forEach((x: qAccessorial) => {
          if (
            (
              ltlQuoteState.quote?.ltlQuote?.deliveryAccessorials ?? []
            ).includes(x.accessorial.name!)
          ) {
            x.checked = true;
          }
        });
        setAccessorials('delivery', sortBy(deliveryVals, 'accessorial.name'));
      case 'Shipment':
        shipmentVals.forEach((x: qAccessorial) => {
          if (
            (
              ltlQuoteState.quote?.ltlQuote?.shipmentAccessorials ?? []
            ).includes(x.accessorial.name!)
          ) {
            x.checked = true;
          }
        });
        shipmentVals.sort((a, b) => {
          const numA = parseInt(a.accessorial.name!.split(' ')[2]);
          const numB = parseInt(b.accessorial.name!.split(' ')[2]);
          return numA - numB;
        });
        setAccessorials('shipment', shipmentVals);
        break;
      default:
    }
  };
};

export const setZipCode = (
  type: 'origin' | 'destination',
  item: Partial<IAddress>,
  setFields2: setType,
) => {
  return () => {
    const setFields = setFields2.setFields;
    setFields(`${type}City`, item.city ?? '');
    setFields(`${type}Country`, remappedCountryCode(item.country));
    setFields(`${type}State`, item.state ?? '');
    setFields(`${type}Zip`, item.zip ?? '');
  };
};
